/**
 * IonWorkingCue — the single "Ion is working…" status line shown while a turn
 * is streaming. It is the SOLE activity signal during a tool call (the text
 * caret is hidden then — see ChatMessage), so it has to read as actively
 * working, not stalled:
 *
 *   ⚡  Ranking 30 candidate cells…  · 4s
 *   │   │                          │
 *   │   │                          └ elapsed counter, appears after a short
 *   │   │                            threshold — an acknowledged wait feels
 *   │   │                            shorter than a silent one
 *   │   └ step-aware label (toolActivityDescriptor) + animated ellipsis
 *   └ pulsing bolt (motion)
 *
 * The elapsed counter ticks once a second while a tool is in flight. When no
 * tool is running (plain text streaming) it just shows the generic phrase with
 * no counter.
 */

import { useEffect, useState } from 'preact/hooks'
import { IconIonBolt } from '../shared/Icons.jsx'
import { runningTool, toolActivityDescriptor } from './toolStatusLabels.js'
import { t } from '../../i18n/index.js'

/** Don't surface the counter for sub-second work — it would flicker "0s/1s"
 *  on every fast tool. Only once a wait is long enough to feel like one. */
const ELAPSED_THRESHOLD_MS = 2000

/** Compact elapsed: "4s", "45s", "1m 5s". Pure — exported for tests. */
export function formatElapsed(ms) {
  if (!Number.isFinite(ms) || ms < 0) return ''
  const totalSec = Math.floor(ms / 1000)
  if (totalSec < 60) return `${totalSec}s`
  const m = Math.floor(totalSec / 60)
  const s = totalSec % 60
  return `${m}m ${s}s`
}

/**
 * @param {object} props
 * @param {Array} props.toolCalls — message.toolCalls from chatStore
 */
export function IonWorkingCue({ toolCalls }) {
  const call = runningTool(toolCalls)
  const startedAt = call && call.startedAt

  // Tick every second while a tool is in flight so the elapsed counter stays
  // live. Keyed on startedAt so a new tool call restarts the counter cleanly;
  // no interval runs during plain text streaming (no running tool).
  const [, setTick] = useState(0)
  useEffect(() => {
    if (!startedAt) return undefined
    const id = setInterval(() => setTick((n) => n + 1), 1000)
    return () => clearInterval(id)
  }, [startedAt])

  const descriptor = toolActivityDescriptor(toolCalls)
  const label = descriptor
    ? t(descriptor.key, descriptor.params || undefined)
    : t('ion.working')

  const elapsedMs = startedAt ? Date.now() - startedAt : 0
  const showElapsed = !!startedAt && elapsedMs >= ELAPSED_THRESHOLD_MS

  return (
    <div class="ion-working" role="status" aria-live="polite">
      <IconIonBolt size={14} className="ion-working__bolt" />
      <span class="ion-working__label">
        {label}
        <span class="ion-working__dots" aria-hidden="true" />
      </span>
      {showElapsed && (
        <span class="ion-working__elapsed">· {formatElapsed(elapsedMs)}</span>
      )}
    </div>
  )
}
